import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, Award, TrendingUp, Clock, Timer } from 'lucide-react';
import { cn } from '@/lib/core/utils';
import { useIsMobile } from '@/hooks/ui/use-mobile';
import { EmployeeStats } from '@/types/insights';

interface EmployeeStatisticsCardProps {
  employeeStats: EmployeeStats;
  setActiveView: (view: string) => void;
  loading?: boolean;
}

const EmployeeStatisticsCard: React.FC<EmployeeStatisticsCardProps> = ({
  employeeStats,
  setActiveView,
  loading = false
}) => {
  const isMobile = useIsMobile();
  if (loading) {
    return (
      <Card className="w-full h-full" style={{ cursor: 'pointer' }}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="text-sm font-medium">Employee Statistics</CardTitle>
          <Users className="h-5 w-5 text-muted-foreground" />
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-center h-full">
            <div className="text-sm text-muted-foreground">Loading...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!employeeStats) {
    return (
      <Card className="w-full h-full" onClick={() => setActiveView('employee-list')} style={{ cursor: 'pointer' }}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="text-sm font-medium">Employee Statistics</CardTitle>
          <Users className="h-5 w-5 text-muted-foreground" />
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-center h-full">
            <div className="text-sm text-muted-foreground">No data available</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full h-full" onClick={() => setActiveView('employee-list')} style={{ cursor: 'pointer' }}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-sm font-medium">Employee Statistics</CardTitle>
        <Users className="h-5 w-5 text-muted-foreground" />
      </CardHeader>
      <CardContent className={cn(
        isMobile ? "space-y-4 p-4" : "space-y-3"
      )}>
        {/* Top Row Stats */}
        <div className={cn(
          "grid gap-3",
          isMobile ? "grid-cols-1" : "grid-cols-2"
        )}>
          <div className={cn(
            "text-center bg-blue-50 dark:bg-blue-950/20 rounded-lg",
            isMobile ? "p-4" : "p-2"
          )}>
            <div className={cn(
              "font-bold text-blue-600 dark:text-blue-400",
              isMobile ? "text-2xl" : "text-xl"
            )}>{employeeStats.totalEmployees}</div>
            <p className={cn(
              "text-muted-foreground",
              isMobile ? "text-sm" : "text-xs"
            )}>Total Employees</p>
          </div>
          <div className={cn(
            "text-center bg-green-50 dark:bg-green-950/20 rounded-lg",
            isMobile ? "p-4" : "p-2"
          )}>
            <div className={cn(
              "font-bold text-green-600 dark:text-green-400",
              isMobile ? "text-2xl" : "text-xl"
            )}>{employeeStats.activeEmployees}</div>
            <p className={cn(
              "text-muted-foreground",
              isMobile ? "text-sm" : "text-xs"
            )}>Active This Week</p>
          </div>
        </div>

        {/* Performance Metrics Grid */}
        <div className={cn(
          "gap-2",
          isMobile ? "grid grid-cols-1" : "grid grid-cols-2"
        )}>
          <div className={cn(
            "flex items-center justify-between bg-muted/20 rounded-lg",
            isMobile ? "p-3 min-h-[3rem]" : "p-2"
          )}>
            <div className="flex items-center gap-2">
              <Award className={cn(
                "text-yellow-500",
                isMobile ? "h-4 w-4" : "h-3 w-3"
              )} />
              <span className={cn(
                "font-medium",
                isMobile ? "text-sm" : "text-xs"
              )}>Top Performer</span>
            </div>
            <span className={cn(
              "font-semibold truncate",
              isMobile ? "text-sm max-w-[120px]" : "text-xs max-w-[60px]"
            )}>{employeeStats.topPerformer}</span>
          </div>

          <div className={cn(
            "flex items-center justify-between bg-muted/20 rounded-lg",
            isMobile ? "p-3 min-h-[3rem]" : "p-2"
          )}>
            <div className="flex items-center gap-2">
              <TrendingUp className={cn(
                "text-green-500",
                isMobile ? "h-4 w-4" : "h-3 w-3"
              )} />
              <span className={cn(
                "font-medium",
                isMobile ? "text-sm" : "text-xs"
              )}>Attendance</span>
            </div>
            <span className={cn(
              "font-semibold",
              isMobile ? "text-sm" : "text-xs"
            )}>{employeeStats.attendanceRate}%</span>
          </div>

          <div className={cn(
            "flex items-center justify-between bg-muted/20 rounded-lg",
            isMobile ? "p-3 min-h-[3rem]" : "p-2"
          )}>
            <div className="flex items-center gap-2">
              <Clock className={cn(
                "text-blue-500",
                isMobile ? "h-4 w-4" : "h-3 w-3"
              )} />
              <span className={cn(
                "font-medium",
                isMobile ? "text-sm" : "text-xs"
              )}>Avg Hours</span>
            </div>
            <span className={cn(
              "font-semibold",
              isMobile ? "text-sm" : "text-xs"
            )}>{employeeStats.averageHoursPerWeek}h</span>
          </div>

          <div className={cn(
            "flex items-center justify-between bg-muted/20 rounded-lg",
            isMobile ? "p-3 min-h-[3rem]" : "p-2"
          )}>
            <div className="flex items-center gap-2">
              <Timer className={cn(
                "text-orange-500",
                isMobile ? "h-4 w-4" : "h-3 w-3"
              )} />
              <span className={cn(
                "font-medium",
                isMobile ? "text-sm" : "text-xs"
              )}>Late Arrivals</span>
            </div>
            <span className={cn(
              "font-semibold",
              isMobile ? "text-sm" : "text-xs"
            )}>{employeeStats.lateArrivals}</span>
          </div>
        </div>

        {/* Bottom Stats */}
        <div className={cn(
          "grid gap-3 pt-2 border-t",
          isMobile ? "grid-cols-1" : "grid-cols-2"
        )}>
          <div className={cn(
            "text-center",
            isMobile ? "p-2" : ""
          )}>
            <div className={cn(
              "font-bold",
              isMobile ? "text-lg" : "text-sm"
            )}>{employeeStats.totalHoursThisWeek}h</div>
            <p className={cn(
              "text-muted-foreground",
              isMobile ? "text-sm" : "text-xs"
            )}>Total Hours</p>
          </div>
          <div className={cn(
            "text-center",
            isMobile ? "p-2" : "p-4"
          )}>
            <div className={cn(
              "font-bold text-orange-600",
              isMobile ? "text-lg" : "text-sm"
            )}>{employeeStats.overtimeHours}h</div>
            <p className={cn(
              "text-muted-foreground",
              isMobile ? "text-sm" : "text-xs"
            )}>Overtime</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default EmployeeStatisticsCard;